import cn from 'classnames';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { HostSettingsState } from 'editor/src/store/hostSettings/types';

import RichTooltip from 'editor/src/component/RichTooltip';
import { useIsMobile } from 'editor/src/component/useDetectDeviceType';

import { isMultiplePrintAreasGuidelinesTouched, setMultiplePrintAreasGuidelinesTouched } from './utils';

import styles from './index.module.scss';

interface Props {
  config: NonNullable<HostSettingsState['multiplePrintAreasGuidelines']>;
}

function MultiplePrintAreasGuidelines({ config }: Props) {
  const { title, description, tooltip } = config;
  const guidelinesBtnRef = useRef<HTMLButtonElement>(null);
  const [guidelinesTooltipOpen, setGuidelinesTooltipOpen] = React.useState(false);
  const guidelinesTouchedRef = useRef(isMultiplePrintAreasGuidelinesTouched());
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const hasTooltip = !!tooltip?.title || !!tooltip?.imageUrl || !!tooltip?.description;

  // after 1 second, a rich tooltip is displayed by default
  useEffect(() => {
    if (!hasTooltip || guidelinesTouchedRef.current) {
      return;
    }

    setTimeout(() => {
      if (!guidelinesTooltipOpen) {
        setGuidelinesTooltipOpen(true);
      }

      setMultiplePrintAreasGuidelinesTouched();
    }, 1000);
  }, []);

  const toggleGuidelinesTooltip = useCallback(() => {
    setGuidelinesTooltipOpen((prev) => !prev);
  }, []);

  const closeGuidelinesTooltip = useCallback(() => {
    setGuidelinesTooltipOpen(false);
  }, []);

  if (isMobile) {
    return null;
  }

  return (
    <div className={cn(styles.infoBoxContainer)}>
      <div className={styles.infoBox}>
        <p>{title}</p>
        <p>
          {description}{' '}
          {hasTooltip && (
            <button ref={guidelinesBtnRef} className={styles.linkButton} onClick={toggleGuidelinesTooltip}>
              {t('Learn more')}
            </button>
          )}
        </p>
      </div>
      {hasTooltip && (
        <RichTooltip
          isVisible={guidelinesTooltipOpen}
          className={styles.guidelinesInfoTooltip}
          triggerBtnRef={guidelinesBtnRef}
          onClose={closeGuidelinesTooltip}
          pointerOffset={24}
        >
          <>
            {tooltip.imageUrl && <img src={tooltip.imageUrl} alt={tooltip.title} className={styles.bannerImage} />}
            <div className={styles.tooltipBody}>
              {!!tooltip.title && (
                <p>
                  <strong>{tooltip.title}</strong>
                </p>
              )}
              {/* eslint-disable react/no-danger */}
              <div dangerouslySetInnerHTML={{ __html: tooltip.description }} />
              {!!tooltip.learnMoreLink?.text && !!tooltip.learnMoreLink?.url && (
                <p>
                  <a
                    href={tooltip.learnMoreLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.guideLink}
                  >
                    {tooltip.learnMoreLink.text}
                  </a>
                </p>
              )}
            </div>
          </>
        </RichTooltip>
      )}
    </div>
  );
}

export default React.memo(MultiplePrintAreasGuidelines);
